<script context="module" lang="ts">
    import type { SvelteComponent } from 'svelte';
    import type { ItemKind } from '@jet-app/music-classical/models/section/Section';

    // Item Components
    import CategoryBrick from '~/components/jet/lockup/CategoryBrick.svelte';
    import EditorsChoice from '~/components/jet/lockup/EditorsChoice.svelte';
    import EllipseLockup from '~/components/jet/lockup/EllipseLockup.svelte';
    import FlowcaseLockup from '~/components/jet/lockup/FlowcaseLockup.svelte';
    import LinkLockup from '~/components/jet/lockup/LinkLockup.svelte';
    import RectangularLinkLockup from '~/components/jet/lockup/RectangularLinkLockup.svelte';
    import ShelfListItem from '~/components/jet/lockup/ShelfListItem.svelte';
    import SquareLockup from '~/components/jet/lockup/SquareLockup.svelte';
    import TrackList from '~/components/jet/TrackList/TrackList.svelte';
    import TrackListFooter from '~/components/jet/TrackList/TrackListFooter.svelte';
    import LibraryTracks from '~/components/library/tracks/Tracks.svelte';

    const ITEM_COMPONENT_MAPPING = {
        artistLockup: SquareLockup,
        brickLockup: CategoryBrick,
        credit: SquareLockup,
        editorsChoiceLockup: EditorsChoice,
        ellipseLockup: EllipseLockup,
        flowcaseLockup: FlowcaseLockup,
        linkLockup: LinkLockup,
        rectangularLinkLockup: RectangularLinkLockup,
        shelfListItem: ShelfListItem,
        squareLockup: SquareLockup,
        trackListFooter: TrackListFooter,
        trackLockup: TrackList,
    };

    function getSectionItemComponent(
        itemKind: ItemKind,
    ): typeof SvelteComponent {
        return ITEM_COMPONENT_MAPPING[itemKind] as typeof SvelteComponent;
    }
</script>

<script lang="ts">
    // Types
    import type { Section } from '@jet-app/music-classical/models/section/Section';

    // Components
    import Shelf from './Shelf.svelte';
    import GridSection from './Grid.svelte';

    // Utils
    import { loggerFor } from '@amp/web-apps-logger';

    export let section: Section<unknown>;

    const log = loggerFor('<Section>');

    $: ariaText = section?.title;
    $: itemKind = section.itemKind;
    $: itemComponent = getSectionItemComponent(itemKind);

    // This is needed as the Section itemKind can't be used to typeguard `items`.
    // We must resort to `as` to resolve this, but CARE SHOULD BE TAKEN when using
    // this to check that the type is the appropriate type for that itemKind.
    function cast<T>(x: unknown): T {
        return x as T;
    }
</script>

<div
    class="section"
    class:section--alternate={section.backgroundTreatment === 'alternate'}
    data-testid="section-container"
    aria-label={ariaText}
>
    <div
        class="section-content"
        class:is-editorial-lockup={itemKind === 'flowcaseLockup'}
        class:is-square-lockup={itemKind === 'squareLockup'}
        class:is-featured-lockup={itemKind === 'editorsChoiceLockup'}
        data-testid="section-content"
    >
        {#if section?.sectionKind === 'shelf' || section?.sectionKind === 'info'}
            <Shelf shelf={cast(section)} {itemComponent} />
        {:else if section?.sectionKind === 'grid'}
            <GridSection
                section={cast(section)}
                {itemComponent}
                customColumnType={itemKind}
            />
        {:else if section?.sectionKind === 'libraryTracks'}
            <LibraryTracks section={cast(section)} />
        {:else if !!itemComponent}
            <svelte:component this={itemComponent} section={cast(section)} />
        {:else}
            {log.warn('unsupported section layout:', section)}
        {/if}
    </div>

    {#if section.displaySeparator}
        <div data-testid="section-separator" class="section-separator" />
    {/if}
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/margin-gutter' as *;

    // used to calculate offset height of shelf items for centering
    // shelf navigation arrows
    $section-padding: 15px;

    .section {
        &:not(:first-of-type) {
            padding-top: 12px;
        }

        :global(
                .header-classical:has(
                        .header-nav--header-type-a:not(
                                .header-nav--with-separator
                            )
                    )
            )
            + &:not(:has(.library-track--header)) {
            padding-top: $spacerB + 8px; // +8px aligns to spec overlay
        }

        // No `padding-top` on Cell Containers
        &:has(.cell-container) {
            padding-top: 19px;

            @media (--small) {
                padding-top: 0;
            }
        }

        &:has(.songs-list) {
            padding-top: 0;

            :global(+ .section:has(.songs-list)) {
                padding-top: 20px;
            }
        }

        &:has(.tracklist-footer) {
            padding-top: 0;
        }
    }

    .section--alternate {
        background-color: var(--opaqueShelfBG);
    }

    .section-separator {
        border-bottom: var(--keyline-border-style);

        @include margin-gutter(0);
    }

    .is-featured-lockup {
        --override-shelf-overflow-bleed-bottom: 20px;
    }
</style>
